import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings as SettingsIcon, Database, Zap, Bell, Shield, Save, Server, Plus, Trash2, Edit, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EnhancedSAPSystemConfig } from '@/components/EnhancedSAPSystemConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const [saved, setSaved] = useState(false);
  const [sapSystems, setSapSystems] = useState([]);
  const [addSystemDialog, setAddSystemDialog] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [newSystem, setNewSystem] = useState({
    name: '',
    type: 'S/4HANA Cloud',
    version: '',
    environment: 'Production',
    url: '',
    client: '100',
    username: '',
    password: '',
    location: 'US-East',
    description: '',
    
    // Environment Architecture
    architecture_type: 'single_system_multi_client', // or 'separate_systems' or 'btp_spaces'
    dev_client: '100',
    qa_client: '200',
    prod_client: '300',
    dev_url: '',
    qa_url: '',
    prod_url: '',
    
    // Access Configuration
    access_types: {
      http_rest: true,
      odata_api: false,
      cloud_foundry_api: false,
      adt_api: false,
      rfc_access: false,
      transport_api: false
    },
    
    // Cloud Foundry Configuration (if BTP)
    cf_api_endpoint: '',
    cf_org: '',
    cf_space_dev: '',
    cf_space_qa: '',
    cf_space_prod: '',
    cf_username: '',
    cf_password: '',
    
    // OData/REST API Configuration
    odata_base_url: '',
    api_username: '',
    api_password: '',
    
    // Transport System Configuration
    transport_domain: '',
    transport_group: ''
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [settings, setSettings] = useState({
    // General Settings
    platformName: 'SAP AI Customization Engine',
    environment: 'production',
    timezone: 'UTC',
    language: 'en',
    
    // Agent Settings
    defaultLLM: 'gpt-4o-mini',
    agentTimeout: 300,
    maxRetries: 3,
    parallelAgents: false,
    
    // Notification Settings
    emailNotifications: true,
    slackNotifications: false,
    webhookUrl: '',
    notifyOnComplete: true,
    notifyOnFailure: true,
    
    // Integration Settings
    serviceNowUrl: 'https://your-instance.service-now.com',
    jiraUrl: 'https://your-domain.atlassian.net',
    sapBtpUrl: 'https://btp.example.com',
    
    // Security Settings
    requireApproval: true,
    twoFactorAuth: false,
    sessionTimeout: 3600,
    auditLogging: true,
    
    // Performance Settings
    cacheEnabled: true,
    cacheDuration: 3600,
    maxConcurrentTasks: 10,
    autoRefresh: true,
    refreshInterval: 5000
  });

  useEffect(() => {
    loadSapSystems();
  }, []);

  const loadSapSystems = async () => {
    try {
      const response = await axios.get(`${API}/settings/sap-systems`);
      setSapSystems(response.data);
    } catch (error) {
      console.error('Error loading SAP systems:', error);
      // Load mock data if backend not available
      setSapSystems([
        {
          id: '1',
          name: 'S/4HANA Production',
          type: 'S/4HANA Cloud',
          version: '2023',
          environment: 'Production',
          url: 'https://s4hana-prod.company.com',
          client: '100',
          location: 'US-East',
          status: 'connected',
          lastConnection: new Date().toISOString()
        },
        {
          id: '2',
          name: 'SAP BTP Production',
          type: 'SAP BTP',
          version: 'Cloud',
          environment: 'Production',
          url: 'https://btp-prod.cfapps.us10.hana.ondemand.com',
          location: 'US-East',
          status: 'connected',
          lastConnection: new Date().toISOString()
        }
      ]);
    }
  };

  const handleAddSystem = async (systemData) => {
    try {
      const systemWithId = { ...systemData, id: Date.now().toString(), status: 'configured' };
      
      // Save to backend
      await axios.post(`${API}/settings/sap-systems`, systemData);
      
      setSapSystems([...sapSystems, systemWithId]);
      setAddSystemDialog(false);
      resetNewSystem();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      // If backend not available, just add locally
      const systemWithId = { ...systemData, id: Date.now().toString(), status: 'configured' };
      setSapSystems([...sapSystems, systemWithId]);
      setAddSystemDialog(false);
      resetNewSystem();
    }
  };

  const handleDeleteSystem = async (id) => {
    try {
      await axios.delete(`${API}/settings/sap-systems/${id}`);
      setSapSystems(sapSystems.filter(s => s.id !== id));
    } catch (error) {
      // If backend not available, just delete locally
      setSapSystems(sapSystems.filter(s => s.id !== id));
    }
  };

  const handleTestConnection = async (system) => {
    setTestingConnection(true);
    setConnectionStatus(null);
    
    try {
      // In production, this would actually test the connection
      await new Promise(resolve => setTimeout(resolve, 2000));
      const success = Math.random() > 0.3; // Mock success/failure
      
      setConnectionStatus({ 
        success, 
        message: success ? 'Connection successful!' : 'Connection failed. Check credentials.' 
      });
      
      if (success) {
        setSapSystems(sapSystems.map(s => 
          s.id === system.id ? { ...s, status: 'connected', lastConnection: new Date().toISOString() } : s
        ));
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: 'Connection test failed' });
    } finally {
      setTestingConnection(false);
      setTimeout(() => setConnectionStatus(null), 5000);
    }
  };

  const resetNewSystem = () => {
    setNewSystem({
      name: '',
      type: 'S/4HANA Cloud',
      version: '',
      environment: 'Production',
      url: '',
      client: '100',
      username: '',
      password: '',
      location: 'US-East',
      description: ''
    });
  };

  const handleSave = () => {
    // In production, this would save to backend
    console.log('Saving settings:', settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-2">Configure platform and integration settings</p>
        </div>
        <Button onClick={handleSave} data-testid="save-settings-btn">
          <Save className="mr-2 h-4 w-4" />
          Save Changes
        </Button>
      </div>

      {saved && (
        <Alert className="bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">Settings saved successfully!</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="sap-landscape" className="w-full">
        <TabsList>
          <TabsTrigger value="sap-landscape">
            <Server className="mr-2 h-4 w-4" />
            SAP Landscape
          </TabsTrigger>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        {/* SAP Landscape Configuration Tab */}
        <TabsContent value="sap-landscape" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5 text-blue-600" />
                    SAP Landscape Configuration
                  </CardTitle>
                  <CardDescription>Configure SAP systems for discovery and integration</CardDescription>
                </div>
                <Dialog open={addSystemDialog} onOpenChange={setAddSystemDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" />
                      Add SAP System
                    </Button>
                  </DialogTrigger>
                </Dialog>
                
                {/* Use Enhanced Config Dialog */}
                <EnhancedSAPSystemConfig
                  open={addSystemDialog}
                  onOpenChange={setAddSystemDialog}
                  system={newSystem}
                  onSave={handleAddSystem}
                />
              </div>
            </CardHeader>
            <CardContent>
              {sapSystems.length === 0 ? (
                <div className="text-center py-12">
                  <Server className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">No SAP Systems Configured</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your SAP systems to enable discovery and integration
                  </p>
                  <Button onClick={() => setAddSystemDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add First System
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {sapSystems.map((system) => (
                    <Card key={system.id} className="border-2">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{system.name}</h3>
                              <Badge variant={
                                system.environment === 'Production' ? 'default' :
                                system.environment === 'Development' ? 'secondary' : 'outline'
                              }>
                                {system.environment}
                              </Badge>
                              {system.status === 'connected' ? (
                                <Badge className="bg-green-600">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Connected
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  Configured
                                </Badge>
                              )}
                              {/* Deployment Readiness Badge */}
                              {system.architecture_type && system.access_types && (
                                <Badge className="bg-blue-600">
                                  <Server className="h-3 w-3 mr-1" />
                                  Deploy Ready
                                </Badge>
                              )}
                              {!system.architecture_type && (
                                <Badge variant="destructive">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Config Incomplete
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <div className="text-muted-foreground text-xs">Type</div>
                                <div className="font-medium">{system.type}</div>
                              </div>
                              {system.version && (
                                <div>
                                  <div className="text-muted-foreground text-xs">Version</div>
                                  <div className="font-medium">{system.version}</div>
                                </div>
                              )}
                              <div>
                                <div className="text-muted-foreground text-xs">Location</div>
                                <div className="font-medium">{system.location}</div>
                              </div>
                              {system.client && (
                                <div>
                                  <div className="text-muted-foreground text-xs">Client</div>
                                  <div className="font-medium">{system.client}</div>
                                </div>
                              )}
                            </div>

                            <div className="text-xs text-muted-foreground">
                              <div className="mb-1">URL: {system.url}</div>
                              {system.lastConnection && (
                                <div>Last Connection: {new Date(system.lastConnection).toLocaleString()}</div>
                              )}
                            </div>
                            
                            {/* Deployment Configuration Details */}
                            {system.architecture_type && (
                              <div className="mt-3 p-3 bg-slate-50 border rounded-lg">
                                <div className="text-xs font-semibold mb-2">Deployment Configuration</div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-muted-foreground">Architecture: </span>
                                    <Badge variant="outline" className="text-xs">
                                      {system.architecture_type === 'single_system_multi_client' ? 'Multi-Client' :
                                       system.architecture_type === 'separate_systems' ? 'Separate Systems' :
                                       system.architecture_type === 'btp_spaces' ? 'BTP Spaces' : system.architecture_type}
                                    </Badge>
                                  </div>
                                  {system.dev_client && (
                                    <div>
                                      <span className="text-muted-foreground">Clients: </span>
                                      <span className="font-mono">{system.dev_client}/{system.qa_client}/{system.prod_client}</span>
                                    </div>
                                  )}
                                </div>
                                {system.access_types && (
                                  <div className="mt-2">
                                    <span className="text-muted-foreground text-xs">APIs: </span>
                                    {Object.entries(system.access_types).filter(([k, v]) => v).map(([key]) => (
                                      <Badge key={key} variant="secondary" className="text-xs ml-1">
                                        {key.replace(/_/g, ' ').toUpperCase()}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleTestConnection(system)}
                              disabled={testingConnection}
                            >
                              {testingConnection ? 'Testing...' : 'Test Connection'}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="icon"
                              onClick={() => handleDeleteSystem(system.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </div>

                        {connectionStatus && (
                          <Alert className={`mt-3 ${connectionStatus.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                            <AlertDescription className={connectionStatus.success ? 'text-green-800' : 'text-red-800'}>
                              {connectionStatus.success ? <CheckCircle className="h-4 w-4 inline mr-2" /> : <XCircle className="h-4 w-4 inline mr-2" />}
                              {connectionStatus.message}
                            </AlertDescription>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <Alert className="mt-4">
                <AlertDescription className="text-xs">
                  <strong>Discovery Integration:</strong> Configured systems will be automatically scanned for:
                  <ul className="list-disc list-inside mt-2 ml-2">
                    <li>Installed SAP modules and components</li>
                    <li>Custom code objects (Z programs, function modules, tables)</li>
                    <li>Clean Core compliance status</li>
                    <li>Integration points and APIs</li>
                    <li>Technical debt analysis</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                General Settings
              </CardTitle>
              <CardDescription>Basic platform configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="platformName">Platform Name</Label>
                <Input
                  id="platformName"
                  value={settings.platformName}
                  onChange={(e) => updateSetting('platformName', e.target.value)}
                  data-testid="platform-name-input"
                />
              </div>

              <div>
                <Label htmlFor="environment">Environment</Label>
                <Select value={settings.environment} onValueChange={(value) => updateSetting('environment', value)}>
                  <SelectTrigger id="environment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="development">Development</SelectItem>
                    <SelectItem value="staging">Staging</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={settings.timezone} onValueChange={(value) => updateSetting('timezone', value)}>
                    <SelectTrigger id="timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                      <SelectItem value="Europe/London">London</SelectItem>
                      <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="language">Language</Label>
                  <Select value={settings.language} onValueChange={(value) => updateSetting('language', value)}>
                    <SelectTrigger id="language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Agent Configuration
              </CardTitle>
              <CardDescription>Configure AI agent behavior and performance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="defaultLLM">Default LLM Model</Label>
                <Select value={settings.defaultLLM} onValueChange={(value) => updateSetting('defaultLLM', value)}>
                  <SelectTrigger id="defaultLLM" data-testid="llm-model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast)</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o (Balanced)</SelectItem>
                    <SelectItem value="gpt-5">GPT-5 (Advanced)</SelectItem>
                    <SelectItem value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet</SelectItem>
                    <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Model used for all agent tasks</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agentTimeout">Agent Timeout (seconds)</Label>
                  <Input
                    id="agentTimeout"
                    type="number"
                    value={settings.agentTimeout}
                    onChange={(e) => updateSetting('agentTimeout', parseInt(e.target.value))}
                  />
                </div>

                <div>
                  <Label htmlFor="maxRetries">Max Retries</Label>
                  <Input
                    id="maxRetries"
                    type="number"
                    value={settings.maxRetries}
                    onChange={(e) => updateSetting('maxRetries', parseInt(e.target.value))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="parallelAgents">Parallel Agent Execution</Label>
                  <p className="text-xs text-muted-foreground">Execute multiple agents simultaneously</p>
                </div>
                <Switch
                  id="parallelAgents"
                  checked={settings.parallelAgents}
                  onCheckedChange={(checked) => updateSetting('parallelAgents', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                External Integrations
              </CardTitle>
              <CardDescription>Configure connections to external systems</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="serviceNowUrl">ServiceNow Instance URL</Label>
                <Input
                  id="serviceNowUrl"
                  value={settings.serviceNowUrl}
                  onChange={(e) => updateSetting('serviceNowUrl', e.target.value)}
                  placeholder="https://your-instance.service-now.com"
                />
              </div>

              <div>
                <Label htmlFor="jiraUrl">Jira Instance URL</Label>
                <Input
                  id="jiraUrl"
                  value={settings.jiraUrl}
                  onChange={(e) => updateSetting('jiraUrl', e.target.value)}
                  placeholder="https://your-domain.atlassian.net"
                />
              </div>

              <div>
                <Label htmlFor="sapBtpUrl">SAP BTP URL</Label>
                <Input
                  id="sapBtpUrl"
                  value={settings.sapBtpUrl}
                  onChange={(e) => updateSetting('sapBtpUrl', e.target.value)}
                  placeholder="https://btp.example.com"
                />
              </div>

              <Alert>
                <AlertDescription className="text-xs">
                  Note: These are mock configurations. In production, proper authentication and API keys would be required.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Settings
              </CardTitle>
              <CardDescription>Configure how and when to receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="emailNotifications">Email Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive updates via email</p>
                </div>
                <Switch
                  id="emailNotifications"
                  checked={settings.emailNotifications}
                  onCheckedChange={(checked) => updateSetting('emailNotifications', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="slackNotifications">Slack Notifications</Label>
                  <p className="text-xs text-muted-foreground">Send alerts to Slack</p>
                </div>
                <Switch
                  id="slackNotifications"
                  checked={settings.slackNotifications}
                  onCheckedChange={(checked) => updateSetting('slackNotifications', checked)}
                />
              </div>

              {settings.slackNotifications && (
                <div>
                  <Label htmlFor="webhookUrl">Slack Webhook URL</Label>
                  <Input
                    id="webhookUrl"
                    value={settings.webhookUrl}
                    onChange={(e) => updateSetting('webhookUrl', e.target.value)}
                    placeholder="https://hooks.slack.com/services/..."
                  />
                </div>
              )}

              <div className="space-y-3 pt-4 border-t">
                <h4 className="text-sm font-medium">Notification Triggers</h4>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="notifyOnComplete">Ticket Completion</Label>
                  <Switch
                    id="notifyOnComplete"
                    checked={settings.notifyOnComplete}
                    onCheckedChange={(checked) => updateSetting('notifyOnComplete', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="notifyOnFailure">Agent Failures</Label>
                  <Switch
                    id="notifyOnFailure"
                    checked={settings.notifyOnFailure}
                    onCheckedChange={(checked) => updateSetting('notifyOnFailure', checked)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Settings
              </CardTitle>
              <CardDescription>Manage security and compliance settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="requireApproval">Require Deployment Approval</Label>
                  <p className="text-xs text-muted-foreground">Manual approval before production deployment</p>
                </div>
                <Switch
                  id="requireApproval"
                  checked={settings.requireApproval}
                  onCheckedChange={(checked) => updateSetting('requireApproval', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="twoFactorAuth">Two-Factor Authentication</Label>
                  <p className="text-xs text-muted-foreground">Require 2FA for all users</p>
                </div>
                <Switch
                  id="twoFactorAuth"
                  checked={settings.twoFactorAuth}
                  onCheckedChange={(checked) => updateSetting('twoFactorAuth', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auditLogging">Comprehensive Audit Logging</Label>
                  <p className="text-xs text-muted-foreground">Log all actions for compliance</p>
                </div>
                <Switch
                  id="auditLogging"
                  checked={settings.auditLogging}
                  onCheckedChange={(checked) => updateSetting('auditLogging', checked)}
                />
              </div>

              <div>
                <Label htmlFor="sessionTimeout">Session Timeout (seconds)</Label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
                />
                <p className="text-xs text-muted-foreground mt-1">Auto logout after inactivity</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance Optimization</CardTitle>
              <CardDescription>Configure performance and caching settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="cacheEnabled">Enable Caching</Label>
                  <p className="text-xs text-muted-foreground">Cache API responses for faster load times</p>
                </div>
                <Switch
                  id="cacheEnabled"
                  checked={settings.cacheEnabled}
                  onCheckedChange={(checked) => updateSetting('cacheEnabled', checked)}
                />
              </div>

              {settings.cacheEnabled && (
                <div>
                  <Label htmlFor="cacheDuration">Cache Duration (seconds)</Label>
                  <Input
                    id="cacheDuration"
                    type="number"
                    value={settings.cacheDuration}
                    onChange={(e) => updateSetting('cacheDuration', parseInt(e.target.value))}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="maxConcurrentTasks">Max Concurrent Tasks</Label>
                <Input
                  id="maxConcurrentTasks"
                  type="number"
                  value={settings.maxConcurrentTasks}
                  onChange={(e) => updateSetting('maxConcurrentTasks', parseInt(e.target.value))}
                />
                <p className="text-xs text-muted-foreground mt-1">Maximum parallel operations</p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="autoRefresh">Auto-Refresh Data</Label>
                  <p className="text-xs text-muted-foreground">Automatically update data in real-time</p>
                </div>
                <Switch
                  id="autoRefresh"
                  checked={settings.autoRefresh}
                  onCheckedChange={(checked) => updateSetting('autoRefresh', checked)}
                />
              </div>

              {settings.autoRefresh && (
                <div>
                  <Label htmlFor="refreshInterval">Refresh Interval (milliseconds)</Label>
                  <Input
                    id="refreshInterval"
                    type="number"
                    value={settings.refreshInterval}
                    onChange={(e) => updateSetting('refreshInterval', parseInt(e.target.value))}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
